import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import {
  createDevopsSession,
  pingDevopsSession,
  streamDevopsMessage,
} from "../api/services/devopsAgentService";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";
import type { StickyEntity } from "../types/devops";
import type { AgentActivityEvent } from "../utils/parseAgentSseStream";

export interface DevopsChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  streaming?: boolean;
  activities?: string[];
}

const WELCOME =
  "Ask about environments, applications, configurations, or releases. I can query the catalog and make changes after you confirm. Bind an entity from a hub to keep this chat focused on that record.";

function withSticky(text: string, sticky: StickyEntity | null): string {
  if (!sticky) return text;
  return `\`\`\`sticky-entity\n${JSON.stringify(sticky, null, 2)}\n\`\`\`\n\n${text}`;
}

function humanizeToolName(name: string): string {
  return name
    .replace(/^cms_/, "")
    .replace(/_/g, " ")
    .replace(/^\w/, (ch) => ch.toUpperCase());
}

function labelForActivity(event: AgentActivityEvent): string | null {
  const type = (event.type ?? "").toUpperCase();
  if (type === "TOOL_CALL" && event.toolName) {
    return `Using ${humanizeToolName(event.toolName)}`;
  }
  if (type === "RUN_STATUS") {
    const status = (event.status ?? "").toLowerCase();
    if (!status || status === "completed") return null;
    return "Working…";
  }
  return null;
}

export function useDevopsAgentChat() {
  const { activeTenantId } = useAuth();
  const { stickyEntity } = useDevopsWorkspace();
  const [messages, setMessages] = useState<DevopsChatMessage[]>([
    { id: "welcome", role: "assistant", content: WELCOME, timestamp: new Date() },
  ]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("devops", tenantRef.current);
      setSessionId(null);
      setContextUsage(null);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    async function restore() {
      if (!activeTenantId) {
        setSessionId(null);
        return;
      }
      const stored = getStoredAgentSession("devops", activeTenantId);
      if (!stored?.sessionId) return;
      try {
        const valid = await pingDevopsSession(stored.sessionId);
        if (cancelled) return;
        if (valid) setSessionId(stored.sessionId);
        else clearStoredAgentSession("devops", activeTenantId);
      } catch {
        if (!cancelled) clearStoredAgentSession("devops", activeTenantId);
      }
    }
    restore();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const id = await createDevopsSession();
    setSessionId(id);
    if (activeTenantId) setStoredAgentSession("devops", activeTenantId, id);
    return id;
  }, [sessionId, activeTenantId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setError(null);
      setLoading(true);
      const userMsg: DevopsChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: DevopsChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
        streaming: true,
        activities: ["Working…"],
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      try {
        const sid = await ensureSession();
        let composingShown = false;
        const result = await streamDevopsMessage(sid, withSticky(trimmed, stickyEntity), {
          onActivity: (event) => {
            const label = labelForActivity(event);
            if (!label) return;
            setMessages((prev) => {
              const currentMsg = prev.find((m) => m.id === assistantId);
              const current = currentMsg?.activities ?? [];
              if (current[current.length - 1] === label) return prev;
              return prev.map((m) =>
                m.id === assistantId ? { ...m, activities: [...current, label] } : m,
              );
            });
          },
          onTextDelta: () => {
            if (composingShown) return;
            composingShown = true;
            setMessages((prev) => {
              const currentMsg = prev.find((m) => m.id === assistantId);
              const current = currentMsg?.activities ?? [];
              if (current[current.length - 1] === "Composing reply…") return prev;
              return prev.map((m) =>
                m.id === assistantId
                  ? { ...m, activities: [...current, "Composing reply…"] }
                  : m,
              );
            });
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: result.response,
                  streaming: false,
                  activities: undefined,
                }
              : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev.map((m) =>
            m.id === assistantId ? { ...m, streaming: false, activities: undefined } : m,
          );
        });
      } finally {
        setLoading(false);
      }
    },
    [ensureSession, stickyEntity],
  );

  return { messages, loading, error, contextUsage, sendMessage };
}

type DevopsChatValue = ReturnType<typeof useDevopsAgentChat>;

const DevopsChatContext = createContext<DevopsChatValue | null>(null);

export const DevopsChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const chat = useDevopsAgentChat();
  return <DevopsChatContext.Provider value={chat}>{children}</DevopsChatContext.Provider>;
};

export function useDevopsChat() {
  const ctx = useContext(DevopsChatContext);
  if (!ctx) {
    throw new Error("useDevopsChat must be used within DevopsChatProvider");
  }
  return ctx;
}
